import React, { useEffect, useState, useMemo } from 'react';
import {
    Users,
    MapPin,
    Search,
    Building2,
    ChevronRight,
    Vote,
    CheckCircle,
    Home,
    Landmark
} from 'lucide-react';
import { ElectionsSEO } from '../components/SEO/ElectionSEO';

// Import des données des candidats locaux
import candidatsData from '../constants/candidats_udb_locales_new.json';

// Types pour TypeScript
type Candidat = {
    numero: number;
    nom: string;
};

type Liste = {
    numero: string | number;
    candidats: Candidat[];
};

type Arrondissement = {
    numero: number;
    nom: string;
    listes: Liste[];
};

type Commune = {
    nom: string;
    arrondissements?: Arrondissement[];
    listes?: Liste[];
};

type Departement = {
    nom: string;
    listes: Liste[];
};

type ProvinceData = {
    code?: string;
    nom?: string;
    communes?: Commune[];
    departements?: Departement[];
};

type CandidatsData = {
    titre?: string;
    annee?: number;
    provinces?: ProvinceData[];
};

const ElectionsLocalesPage: React.FC = () => {
    // Cast des données pour TypeScript
    const typedCandidatsData = candidatsData as CandidatsData;

    // Extraire les provinces
    const provinces = (typedCandidatsData?.provinces || []).map(p => p?.nom || 'Province inconnue');

    const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [expandedProvinces, setExpandedProvinces] = useState<Set<string>>(new Set());
    const [expandedCommunes, setExpandedCommunes] = useState<Set<string>>(new Set());
    const [expandedDepartements, setExpandedDepartements] = useState<Set<string>>(new Set());
    const [expandedArrondissements, setExpandedArrondissements] = useState<Set<string>>(new Set());
    const [visibleProvinces, setVisibleProvinces] = useState<Set<string>>(new Set());

    const getProvinceImageName = (provinceName: string): string => {
        const imageMap: { [key: string]: string } = {
            'Estuaire': 'G1.jpg',
            'Haut-Ogooue': 'G2.jpg',
            'Moyen-Ogooue': 'G3.jpg',
            'Ngounie': 'G4.jpg',
            'Nyanga': 'G5.jpg',
            'Ogooué-Ivindo': 'G6.jpg',
            'Ogooué-Lolo': 'G7.jpg',
            'Ogooué-Maritime': 'G8.jpg',
            'Woleu-Ntem': 'G9.jpg'
        };
        return imageMap[provinceName] || 'default.jpg';
    };

    const getProvinceShortName = (provinceName: string): string => {
        if (!provinceName) return 'Province inconnue';
        return provinceName.replace('PROVINCE DE ', '').replace('PROVINCE DU ', '').replace('PROVINCE DE LA ', '');
    };

    useEffect(() => {
        // Animation progressive d'apparition des provinces
        const timer = setTimeout(() => {
            provinces.forEach((province, index) => {
                setTimeout(() => {
                    setVisibleProvinces(prev => {
                        const newSet = new Set(prev);
                        newSet.add(province);
                        return newSet;
                    });
                }, index * 150);
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [provinces]);

    // Filtrage des données avec recherche
    const filteredData = useMemo(() => {
        const filtered: ProvinceData[] = [];
        const provincesArray = typedCandidatsData?.provinces || [];

        provincesArray.forEach((province: ProvinceData) => {
            if (!province) return;

            const provinceName = province.nom || 'Province inconnue';
            if (selectedProvince && selectedProvince !== provinceName) return;

            const filteredProvince: ProvinceData = {
                code: province.code || '',
                nom: provinceName,
                communes: [],
                departements: []
            };

            let hasMatches = false;

            // Filtrer les communes
            if (province.communes && Array.isArray(province.communes)) {
                province.communes.forEach((commune: Commune) => {
                    if (!commune) return;

                    let filteredCommune: Commune | null = null;

                    // Communes avec arrondissements
                    if (commune.arrondissements && Array.isArray(commune.arrondissements)) {
                        const filteredArrondissements = commune.arrondissements.map((arr: Arrondissement) => {
                            if (!arr || !arr.listes) return null;

                            const filteredListes = arr.listes.map((liste: Liste) => {
                                if (!liste || !liste.candidats) return { ...liste, candidats: [] };

                                return {
                                    ...liste,
                                    candidats: !searchTerm.trim() ? liste.candidats : liste.candidats.filter((candidat: Candidat) => {
                                        if (!candidat) return false;
                                        const searchLower = searchTerm.toLowerCase();
                                        return (
                                            provinceName.toLowerCase().includes(searchLower) ||
                                            (commune.nom || '').toLowerCase().includes(searchLower) ||
                                            (arr.nom || '').toLowerCase().includes(searchLower) ||
                                            (candidat.nom || '').toLowerCase().includes(searchLower) ||
                                            (candidat.numero || '').toString().includes(searchLower)
                                        );
                                    })
                                };
                            }).filter((liste: Liste) => liste.candidats && liste.candidats.length > 0);

                            if (filteredListes.length > 0 || !searchTerm.trim()) {
                                return { ...arr, listes: filteredListes };
                            }
                            return null;
                        }).filter(Boolean);

                        if (filteredArrondissements.length > 0 || !searchTerm.trim()) {
                            filteredCommune = {
                                nom: commune.nom,
                                arrondissements: filteredArrondissements as Arrondissement[]
                            };
                        }
                    }
                    // Communes sans arrondissements (avec listes directes)
                    else if (commune.listes && Array.isArray(commune.listes)) {
                        const filteredListes = commune.listes.map((liste: Liste) => {
                            if (!liste || !liste.candidats) return { ...liste, candidats: [] };

                            return {
                                ...liste,
                                candidats: !searchTerm.trim() ? liste.candidats : liste.candidats.filter((candidat: Candidat) => {
                                    if (!candidat) return false;
                                    const searchLower = searchTerm.toLowerCase();
                                    return (
                                        provinceName.toLowerCase().includes(searchLower) ||
                                        (commune.nom || '').toLowerCase().includes(searchLower) ||
                                        (candidat.nom || '').toLowerCase().includes(searchLower) ||
                                        (candidat.numero || '').toString().includes(searchLower)
                                    );
                                })
                            };
                        }).filter((liste: Liste) => liste.candidats && liste.candidats.length > 0);

                        if (filteredListes.length > 0 || !searchTerm.trim()) {
                            filteredCommune = {
                                nom: commune.nom,
                                listes: filteredListes
                            };
                        }
                    }

                    if (filteredCommune) {
                        filteredProvince.communes!.push(filteredCommune);
                        hasMatches = true;
                    }
                });
            }

            // Filtrer les départements
            if (province.departements && Array.isArray(province.departements)) {
                province.departements.forEach((departement: Departement) => {
                    if (!departement || !departement.listes) return;

                    const filteredDepartement: Departement = {
                        nom: departement.nom || 'Département inconnu',
                        listes: departement.listes.map((liste: Liste) => {
                            if (!liste || !liste.candidats) return { ...liste, candidats: [] };

                            return {
                                ...liste,
                                candidats: !searchTerm.trim() ? liste.candidats : liste.candidats.filter((candidat: Candidat) => {
                                    if (!candidat) return false;
                                    const searchLower = searchTerm.toLowerCase();
                                    return (
                                        provinceName.toLowerCase().includes(searchLower) ||
                                        (departement.nom || '').toLowerCase().includes(searchLower) ||
                                        (candidat.nom || '').toLowerCase().includes(searchLower) ||
                                        (candidat.numero || '').toString().includes(searchLower)
                                    );
                                })
                            };
                        }).filter((liste: Liste) => liste.candidats && liste.candidats.length > 0)
                    };

                    if (!searchTerm.trim() || filteredDepartement.listes.length > 0) {
                        filteredProvince.departements!.push(filteredDepartement);
                        hasMatches = true;
                    }
                });
            }

            if (hasMatches || !searchTerm.trim()) {
                filtered.push(filteredProvince);
            }
        });

        return filtered;
    }, [searchTerm, selectedProvince, typedCandidatsData]);

    // Calcul des statistiques
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const stats = useMemo(() => {
        let totalCandidats = 0;
        let totalWomen = 0;
        let totalCommunes = 0;
        let totalDepartements = 0;

        const provincesArray = typedCandidatsData?.provinces || [];

        provincesArray.forEach((province: ProvinceData) => {
            if (!province) return;

            // Communes
            if (province.communes && Array.isArray(province.communes)) {
                totalCommunes += province.communes.length;
                province.communes.forEach((commune: Commune) => {
                    if (!commune) return;

                    if (commune.arrondissements) {
                        commune.arrondissements.forEach((arr: Arrondissement) => {
                            if (!arr || !arr.listes) return;
                            arr.listes.forEach((liste: Liste) => {
                                if (!liste || !liste.candidats) return;
                                liste.candidats.forEach((candidat: Candidat) => {
                                    if (!candidat) return;
                                    totalCandidats++;
                                    const nom = candidat.nom || '';
                                    if (nom.toLowerCase().includes('epse') ||
                                        nom.toLowerCase().includes('épouse') ||
                                        nom.match(/^(marie|chantal|edwige|huguette|aurelie|jeannette|brigitte)/i)) {
                                        totalWomen++;
                                    }
                                });
                            });
                        });
                    } else if (commune.listes) {
                        commune.listes.forEach((liste: Liste) => {
                            if (!liste || !liste.candidats) return;
                            liste.candidats.forEach((candidat: Candidat) => {
                                if (!candidat) return;
                                totalCandidats++;
                                const nom = candidat.nom || '';
                                if (nom.toLowerCase().includes('epse') ||
                                    nom.toLowerCase().includes('épouse') ||
                                    nom.match(/^(marie|chantal|edwige|huguette|aurelie|jeannette|brigitte)/i)) {
                                    totalWomen++;
                                }
                            });
                        });
                    }
                });
            }

            // Départements
            if (province.departements && Array.isArray(province.departements)) {
                totalDepartements += province.departements.length;
                province.departements.forEach((departement: Departement) => {
                    if (!departement || !departement.listes) return;
                    departement.listes.forEach((liste: Liste) => {
                        if (!liste || !liste.candidats) return;
                        liste.candidats.forEach((candidat: Candidat) => {
                            if (!candidat) return;
                            totalCandidats++;
                            const nom = candidat.nom || '';
                            if (nom.toLowerCase().includes('epse') ||
                                nom.toLowerCase().includes('épouse') ||
                                nom.match(/^(marie|chantal|edwige|huguette|aurelie|jeannette|brigitte)/i)) {
                                totalWomen++;
                            }
                        });
                    });
                });
            }
        });

        const totalMen = totalCandidats - totalWomen;

        return {
            totalCandidats,
            totalWomen,
            totalMen,
            womenPercentage: totalCandidats > 0 ? Math.round((totalWomen / totalCandidats) * 100) : 0,
            menPercentage: totalCandidats > 0 ? Math.round((totalMen / totalCandidats) * 100) : 0,
            totalProvinces: provincesArray.length,
            totalCommunes,
            totalDepartements
        };
    }, [typedCandidatsData]);

    const toggleProvince = (provinceName: string) => {
        setExpandedProvinces(prev => {
            const newSet = new Set(prev);
            if (newSet.has(provinceName)) {
                newSet.delete(provinceName);
            } else {
                newSet.add(provinceName);
            }
            return newSet;
        });
    };

    const toggleCommune = (communeId: string) => {
        setExpandedCommunes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(communeId)) {
                newSet.delete(communeId);
            } else {
                newSet.add(communeId);
            }
            return newSet;
        });
    };

    const toggleDepartement = (departementId: string) => {
        setExpandedDepartements(prev => {
            const newSet = new Set(prev);
            if (newSet.has(departementId)) {
                newSet.delete(departementId);
            } else {
                newSet.add(departementId);
            }
            return newSet;
        });
    };

    const toggleArrondissement = (arrondissementId: string) => {
        setExpandedArrondissements(prev => {
            const newSet = new Set(prev);
            if (newSet.has(arrondissementId)) {
                newSet.delete(arrondissementId);
            } else {
                newSet.add(arrondissementId);
            }
            return newSet;
        });
    };

    const getProvinceColor = (index: number) => {
        const colors = [
            { bg: 'bg-primary-blue', text: 'text-primary-blue', border: 'border-primary-blue', light: 'bg-primary-blue/10' },
            { bg: 'bg-primary-green', text: 'text-primary-green', border: 'border-primary-green', light: 'bg-primary-green/10' }
        ];
        return colors[index % colors.length];
    };

    const renderCandidatesTable = (candidats: Candidat[]) => (
        <div className="overflow-x-auto">
            <table className="w-full bg-white border border-gray-300">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300">
                        N°
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300">
                        Nom du Candidat
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-300">
                        Statut
                    </th>
                </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                {candidats.map((candidat, index) => {
                    const isHeadOfList = candidat.numero === 1;
                    return (
                        <tr key={index} className={`transition-colors duration-200 ${
                            isHeadOfList
                                ? 'bg-gradient-to-r from-primary-blue/10 to-primary-green/5 hover:from-primary-blue/15 hover:to-primary-green/10 border-l-4 border-primary-blue'
                                : 'hover:bg-gray-50'
                        }`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        isHeadOfList
                                            ? 'bg-gradient-to-r from-primary-blue to-primary-green text-white shadow-md'
                                            : 'bg-primary-blue text-white'
                                    }`}>
                                        {candidat.numero || 'N/A'}
                                        {isHeadOfList && <span className="ml-1">★</span>}
                                    </span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    {isHeadOfList ? (
                                        <div className="w-5 h-5 text-primary-blue mr-3 flex-shrink-0">
                                            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                                            </svg>
                                        </div>
                                    ) : (
                                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                                    )}
                                    <div>
                                        <div className={`text-sm ${isHeadOfList ? 'font-bold text-primary-blue' : 'font-medium text-gray-900'}`}>
                                            {candidat.nom || 'Nom non disponible'}
                                        </div>
                                        {isHeadOfList && (
                                            <div className="text-xs text-primary-green font-semibold uppercase tracking-wide">
                                                Tête de liste
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        isHeadOfList
                                            ? 'bg-gradient-to-r from-primary-blue to-primary-green text-white'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {isHeadOfList ? 'Tête de liste' : 'Candidat'}
                                    </span>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );

    const renderArrondissement = (arrondissement: Arrondissement, communeName: string, provinceName: string) => {
        const arrondissementId = `${provinceName}-${communeName}-${arrondissement.nom}`;
        const isExpanded = expandedArrondissements.has(arrondissementId);

        const totalCandidats = arrondissement.listes?.reduce((sum, liste) =>
            sum + (liste.candidats?.length || 0), 0) || 0;

        return (
            <div key={arrondissement.nom} className="bg-gray-50 border border-gray-200 mb-3 rounded">
                <div
                    className="p-4 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleArrondissement(arrondissementId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <MapPin className="w-4 h-4 text-primary-blue" />
                            <div>
                                <h5 className="font-semibold text-gray-800">{arrondissement.nom}</h5>
                                <p className="text-xs text-gray-600">{totalCandidats} candidats</p>
                            </div>
                        </div>
                        <ChevronRight
                            className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                                isExpanded ? 'rotate-90' : ''
                            }`}
                        />
                    </div>
                </div>

                {isExpanded && (
                    <div className="px-4 pb-4">
                        {arrondissement.listes?.map((liste, listeIndex) => (
                            <div key={listeIndex} className="mt-4">

                                {renderCandidatesTable(liste.candidats || [])}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderCommune = (commune: Commune, provinceName: string, colors: any) => {
        const communeId = `${provinceName}-${commune.nom}`;
        const isExpanded = expandedCommunes.has(communeId);

        let totalCandidats = 0;
        if (commune.arrondissements) {
            totalCandidats = commune.arrondissements.reduce((sum, arr) =>
                sum + (arr.listes?.reduce((lSum, liste) =>
                    lSum + (liste.candidats?.length || 0), 0) || 0), 0);
        } else if (commune.listes) {
            totalCandidats = commune.listes.reduce((sum, liste) =>
                sum + (liste.candidats?.length || 0), 0);
        }

        return (
            <div key={commune.nom} className="bg-white border border-gray-200 mb-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div
                    className="p-6 cursor-pointer hover:bg-gray-50 border-l-4 border-primary-green rounded-l-lg"
                    onClick={() => toggleCommune(communeId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-primary-green/10 border border-primary-green/20 rounded-lg flex items-center justify-center">
                                <Home className="w-6 h-6 text-primary-green" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg">{commune.nom || 'Commune inconnue'}</h4>
                                <p className="text-sm text-gray-600 font-medium">
                                    Commune • {totalCandidats} candidats
                                    {commune.arrondissements && ` • ${commune.arrondissements.length} arrondissements`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-center">
                                <span className="text-primary-green font-bold text-2xl">{totalCandidats}</span>
                                <p className="text-xs text-gray-500 font-medium">candidats</p>
                            </div>
                            <ChevronRight
                                className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                    isExpanded ? 'rotate-90' : ''
                                }`}
                            />
                        </div>
                    </div>
                </div>

                {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50/50">
                        {/* Si la commune a des arrondissements */}
                        {commune.arrondissements ? (
                            <div className="mt-4">
                                <h5 className="font-semibold text-gray-700 mb-3 flex items-center">
                                    <Building2 className="w-4 h-4 mr-2 text-primary-blue" />
                                    Arrondissements
                                </h5>
                                {commune.arrondissements.map((arr) =>
                                    renderArrondissement(arr, commune.nom, provinceName)
                                )}
                            </div>
                        ) : (
                            /* Si la commune a des listes directes */
                            commune.listes?.map((liste, listeIndex) => (
                                <div key={listeIndex} className="mt-6">
                                    <h5 className="font-semibold text-gray-800 mb-4 flex items-center bg-white p-3 rounded-lg border border-gray-200">
                                        <Users className="w-5 h-5 mr-3 text-primary-green" />
                                        Liste N° {liste.numero}
                                        <span className="ml-auto text-sm text-gray-500 font-normal">
                                            {liste.candidats?.length || 0} candidats
                                        </span>
                                    </h5>
                                    {renderCandidatesTable(liste.candidats || [])}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderDepartement = (departement: Departement, provinceName: string, colors: any) => {
        const departementId = `${provinceName}-${departement.nom}`;
        const isExpanded = expandedDepartements.has(departementId);

        const totalCandidats = departement.listes?.reduce((sum, liste) =>
            sum + (liste.candidats?.length || 0), 0) || 0;

        return (
            <div key={departement.nom} className="bg-white border border-gray-200 mb-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
                <div
                    className="p-6 cursor-pointer hover:bg-gray-50 border-l-4 border-primary-blue rounded-l-lg"
                    onClick={() => toggleDepartement(departementId)}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-primary-blue/10 border border-primary-blue/20 rounded-lg flex items-center justify-center">
                                <Landmark className="w-6 h-6 text-primary-blue" />
                            </div>
                            <div>
                                <h4 className="font-bold text-gray-900 text-lg">{departement.nom || 'Département inconnu'}</h4>
                                <p className="text-sm text-gray-600 font-medium">Département • {totalCandidats} candidats</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="text-center">
                                <span className="text-primary-blue font-bold text-2xl">{totalCandidats}</span>
                                <p className="text-xs text-gray-500 font-medium">candidats</p>
                            </div>
                            <ChevronRight
                                className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                    isExpanded ? 'rotate-90' : ''
                                }`}
                            />
                        </div>
                    </div>
                </div>

                {isExpanded && (
                    <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50/50">
                        {departement.listes?.map((liste, listeIndex) => (
                            <div key={listeIndex} className="mt-6">
                                <h5 className="font-semibold text-gray-800 mb-4 flex items-center bg-white p-3 rounded-lg border border-gray-200">
                                    <Users className="w-5 h-5 mr-3 text-primary-green" />
                                    Liste N° {liste.numero}
                                    <span className="ml-auto text-sm text-gray-500 font-normal">
                                        {liste.candidats?.length || 0} candidats
                                    </span>
                                </h5>
                                {renderCandidatesTable(liste.candidats || [])}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <ElectionsSEO type="locales" />
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white pt-16">
                {/* Hero section */}
                <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 z-0">
                        <div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{
                                backgroundImage: `url('/images/elections-bg.jpg')`,
                            }}
                        />
                        <div className="absolute inset-0 bg-primary-blue/80"></div>
                        <div className="absolute inset-0 bg-black/30"></div>
                    </div>

                    <div className="relative z-10 container mx-auto px-4 text-center text-white">
                        <div className="inline-flex items-center space-x-2 text-primary-yellow text-sm mb-6 bg-white/10 backdrop-blur-sm px-4 py-2 border border-white/20">
                            <Vote className="w-4 h-4"/>
                            <span className="font-medium">Élections Locales 2025</span>
                        </div>

                        <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
                            Nos candidats
                            <span className="block text-primary-yellow font-normal">aux Locales</span>
                        </h1>

                        <div className="w-20 h-1 bg-primary-yellow mx-auto mb-8"></div>

                        <p className="text-xl text-white/90 font-light max-w-3xl mx-auto mb-12">
                            Découvrez les candidats de l'UDB qui portent notre vision
                            du Gabon nouveau dans les 9 provinces
                        </p>
                    </div>
                </div>

                {/* Section principale */}
                <section className="py-16 relative overflow-hidden">
                    <div className="container mx-auto px-4 relative z-10 pt-8">
                        <div className="max-w-7xl mx-auto">
                            {/* Navigation et filtres */}
                            <div className="mb-12 space-y-6">
                                {/* Navigation rapide par provinces */}
                                <div className="bg-white/95 backdrop-blur-sm p-6 border border-gray-300">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                        <MapPin className="w-5 h-5 mr-2 text-primary-blue" />
                                        Navigation rapide par provinces
                                    </h3>
                                    <div className="grid grid-cols-3 md:grid-cols-9 gap-4">
                                        {provinces.map((province, index) => {
                                            const isSelected = selectedProvince === province;
                                            const colors = getProvinceColor(index);
                                            const imageCode = getProvinceImageName(province).replace('.jpg', '');
                                            const shortName = getProvinceShortName(province);

                                            return (
                                                <button
                                                    key={province}
                                                    onClick={() => setSelectedProvince(isSelected ? null : province)}
                                                    className={`group relative p-4 border-2 transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                                                        isSelected
                                                            ? `${colors.bg} text-white border-transparent shadow-lg scale-105`
                                                            : `bg-white ${colors.text} ${colors.border} hover:${colors.bg} hover:text-white hover:border-transparent`
                                                    }`}
                                                >
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <div className={`w-14 h-14 rounded-full overflow-hidden border-2 transition-colors ${
                                                            isSelected ? 'border-white/30' : 'border-gray-300 group-hover:border-white/30'
                                                        }`}>
                                                            <img
                                                                src={`/images/provinces/${getProvinceImageName(province)}`}
                                                                alt={`Écusson ${shortName}`}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.currentTarget.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#e5e7eb"/><text x="12" y="16" text-anchor="middle" font-size="10" fill="#374151">${imageCode}</text></svg>`)}`;
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-xs font-bold mb-1">{imageCode}</div>
                                                            <div className="text-xs leading-tight">{shortName}</div>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Recherche et filtres */}
                                <div className="flex flex-col lg:flex-row gap-6 items-center">
                                    <div className="relative flex-1 max-w-lg">
                                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher un candidat, circonscription..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-12 pr-4 py-4 border border-gray-400 focus:outline-none focus:border-primary-blue bg-white/95 backdrop-blur-sm text-base"
                                        />
                                    </div>

                                    <div className="flex items-center space-x-4">
                                        {(selectedProvince || searchTerm) && (
                                            <button
                                                onClick={() => {
                                                    setSelectedProvince(null);
                                                    setSearchTerm('');
                                                }}
                                                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-400 hover:bg-gray-50 transition-colors"
                                            >
                                                Réinitialiser
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Contenu principal */}
                            <div className="space-y-8">
                                {filteredData.map((province: ProvinceData, provinceIndex) => {
                                    const colors = getProvinceColor(provinceIndex);
                                    const provinceName = province.nom || 'Province inconnue';
                                    const isVisible = visibleProvinces.has(provinceName);
                                    const isExpanded = expandedProvinces.has(provinceName);
                                    const shortName = getProvinceShortName(provinceName);

                                    // Calculer le total des candidats
                                    let totalCandidats = 0;

                                    // Compter les candidats dans les communes
                                    province.communes?.forEach(commune => {
                                        if (commune.arrondissements) {
                                            commune.arrondissements.forEach(arr => {
                                                arr.listes?.forEach(liste => {
                                                    totalCandidats += liste.candidats?.length || 0;
                                                });
                                            });
                                        } else if (commune.listes) {
                                            commune.listes.forEach(liste => {
                                                totalCandidats += liste.candidats?.length || 0;
                                            });
                                        }
                                    });

                                    // Compter les candidats dans les départements
                                    province.departements?.forEach(dept => {
                                        dept.listes?.forEach(liste => {
                                            totalCandidats += liste.candidats?.length || 0;
                                        });
                                    });

                                    return (
                                        <div
                                            key={provinceName}
                                            className={`transition-all duration-700 ${
                                                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                            }`}
                                        >
                                            {/* En-tête de la province */}
                                            <div
                                                className={`${colors.bg} text-white p-8 cursor-pointer hover:opacity-95 transition-all duration-300 rounded-t-lg shadow-lg`}
                                                onClick={() => toggleProvince(provinceName)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-6">
                                                        <div className="w-20 h-20 bg-white/15 border-2 border-white/30 rounded-xl flex items-center justify-center overflow-hidden shadow-inner">
                                                            <img
                                                                src={`/images/provinces/${getProvinceImageName(provinceName)}`}
                                                                alt={`Écusson ${shortName}`}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = 'none';
                                                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                                                    if (fallback) fallback.style.display = 'flex';
                                                                }}
                                                            />
                                                            <MapPin className="w-10 h-10 hidden" />
                                                        </div>
                                                        <div>
                                                            <h2 className="text-3xl font-bold mb-2">{shortName}</h2>
                                                            <p className="text-white/90 text-lg font-medium">{totalCandidats} candidats</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center space-x-6">
                                                        <div className="text-center bg-white/10 rounded-lg p-4">
                                                            <div className="text-4xl font-bold mb-1">{totalCandidats}</div>
                                                            <div className="text-white/80 text-sm font-medium uppercase tracking-wide">candidats</div>
                                                        </div>
                                                        <ChevronRight
                                                            className={`w-7 h-7 transition-transform duration-300 ${
                                                                isExpanded ? 'rotate-90' : ''
                                                            }`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contenu de la province */}
                                            {isExpanded && (
                                                <div className="bg-white border-2 border-gray-200 border-t-0 p-8 rounded-b-lg shadow-lg">
                                                    {/* Communes */}
                                                    {province.communes && province.communes.length > 0 && (
                                                        <div className="mb-10">
                                                            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center bg-gradient-to-r from-primary-green/10 to-transparent p-4 rounded-lg border-l-4 border-primary-green">
                                                                <Home className="w-6 h-6 mr-3 text-primary-green" />
                                                                Communes
                                                                <span className="ml-3 text-sm font-normal text-gray-600">
                                                                    ({province.communes.length})
                                                                </span>
                                                            </h3>
                                                            <div className="space-y-6">
                                                                {province.communes.map((commune: Commune) =>
                                                                    renderCommune(commune, provinceName, colors)
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Départements */}
                                                    {province.departements && province.departements.length > 0 && (
                                                        <div>
                                                            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center bg-gradient-to-r from-primary-blue/10 to-transparent p-4 rounded-lg border-l-4 border-primary-blue">
                                                                <Landmark className="w-6 h-6 mr-3 text-primary-blue" />
                                                                Départements
                                                                <span className="ml-3 text-sm font-normal text-gray-600">
                                                                    ({province.departements.length})
                                                                </span>
                                                            </h3>
                                                            <div className="space-y-6">
                                                                {province.departements.map((departement: Departement) =>
                                                                    renderDepartement(departement, provinceName, colors)
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Message si aucun résultat */}
                                {searchTerm && filteredData.length === 0 && (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Search className="w-8 h-8 text-gray-400" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun résultat trouvé</h3>
                                        <p className="text-gray-600 mb-4">
                                            Aucun candidat ou circonscription ne correspond à votre recherche "{searchTerm}"
                                        </p>
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="bg-primary-blue text-white px-6 py-2 font-semibold hover:bg-primary-blue/90 transition-colors"
                                        >
                                            Effacer la recherche
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Call to Action */}
                            <div className="mt-16 bg-gradient-to-r from-primary-blue to-primary-green text-white p-8 text-center">
                                <h3 className="text-2xl font-bold mb-4">Mobilisons-nous pour la Victoire !</h3>
                                <p className="text-white/90 mb-6 max-w-2xl mx-auto">
                                    Soutenez nos candidats aux élections locales et participez à la construction du Gabon nouveau.
                                    Votre voix compte pour l'avenir de nos territoires.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a href="/adhesion" className="bg-primary-yellow text-gray-900 px-8 py-3 font-semibold hover:bg-primary-yellow/90 transition-colors inline-flex items-center space-x-2">
                                        <Users className="w-5 h-5" />
                                        <span>Rejoindre le mouvement</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default ElectionsLocalesPage;
